import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { InvoiceGenerationResult, UploadMonthlyInvoicePayload } from './types';

const getEndpoint = (supplierId: number) => `ums/v1/cms/reconciliation/suppliers/${supplierId}/invoices/monthly/upload`;

type TData = InvoiceGenerationResult;
type TError = AxiosError;
type TVariables = UploadMonthlyInvoicePayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/** Registers a Finance-issued monthly invoice PDF in place of a generated one. Closed months
 * are allowed by design. BE errors to surface verbatim: 400 malformed number/net amount,
 * 404 no GPO profile, 409 number already in use or the existing monthly invoice is paid,
 * 422 the file is missing, too large, or not a PDF. */
export function useUploadMonthlyInvoiceApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ month, supplierId, invoiceNumber, netAmount, file }: TVariables) => {
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('invoiceNumber', invoiceNumber);
      body.append('netAmount', String(netAmount));

      const { data } = await http.post(getEndpoint(supplierId), body, {
        params: { month },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data?.data ?? data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { ...options, mutationKey: ['reconciliation-upload-monthly-invoice'] });
}
